import { IDataProviderFilter } from 'core-components/types'
import { getAppId } from 'src/core/classes/helpers/getAppId'
import { isLovServiceAvailable } from 'src/core/classes/helpers/isCoreServiceAvailable'
import { proxyLov } from 'src/core/classes/helpers/UrlHelper'
import { Rest } from 'src/core/classes/http'
import { IDropdownLOV } from 'src/core/classes/models/IDropdownLOV'
import { IProcedureFetcherSpec } from 'src/core/types'

// LOV served by the configured LOV service (`/lov/*`), reachable via the
// `lovService` URL (`proxyLov`). This is an alternative to the legacy
// sitebuilder dropdown-lov handlers in `debugger-lov.ts`; it is wired in only
// when the app configures `lovService` (see InitializationServiceCore), so
// other consumers of core keep their existing behavior.
//
// NB: the LovController declares `version: '1'`, but the service does not enable
// NestJS URI versioning, so the routes are served at `/lov/*` (no `/v1` prefix).
//
// Differences from the sitebuilder endpoint: the list response is a bare
// `{ data, total }` (no `{ status, data, meta }` envelope) and create/update/
// delete operate on a single item, so the bulk handlers below loop over the
// models. AppId is sent on create (same source as the read filter, getAppId())
// so created values stay visible to the app-scoped dropdown reads.
const LOV_URL = '/lov'

/**
 * Identifier used to address a single LOV row for read-by-id / update / delete.
 * The LOV service keys by the numeric EntryId; the legacy sitebuilder path
 * keys by UID. Consumers must use this so both backends keep working.
 */
export const getLovKeyField = (): 'EntryId' | 'UID' => {
  return isLovServiceAvailable() ? 'EntryId' : 'UID'
}

export const lovServiceFetcher: IProcedureFetcherSpec<IDropdownLOV>['readFromTo'] = async (
  filter,
  sort,
  paging
) => {
  const url = proxyLov(`${LOV_URL}/list`)
  const response = await Rest.post({ url, payload: { filter, sort, paging }, identityToken: true })

  if (!response || !Array.isArray(response.data)) {
    return { Data: [], Errors: response?.message || '' }
  }

  return { Data: response.data, Total: response.total ?? response.data.length, Errors: '' }
}

export const updateLovServiceItem: IProcedureFetcherSpec<IDropdownLOV>['updateHandler'] = async ({
  models,
}) => {
  const url = proxyLov(LOV_URL)
  const results: IDropdownLOV[] = []

  for (const model of models) {
    const response = await Rest.put({ url, payload: model, identityToken: true })
    if (response?.EntryId) {
      results.push(response)
    }
  }

  return results
}

export const createLovServiceItem: IProcedureFetcherSpec<IDropdownLOV>['createHandler'] = async ({
  models,
}) => {
  const url = proxyLov(LOV_URL)
  const results: IDropdownLOV[] = []

  for (const model of models) {
    const payload = { ...model, AppId: getAppId() }
    const response = await Rest.post({ url, payload, identityToken: true })
    if (response?.EntryId) {
      results.push(response)
    }
  }

  return results
}

export const fetchLovKeys = async (filter: IDataProviderFilter): Promise<string[]> => {
  const url = proxyLov(`${LOV_URL}/keys`)
  const response = await Rest.post({ url, payload: { filter }, identityToken: true })

  if (!response || !Array.isArray(response)) {
    return []
  }

  return response
}

export const deleteLovServiceItem: IProcedureFetcherSpec<IDropdownLOV>['deleteHandler'] = async ({
  uids,
}) => {
  let success = true

  for (const id of uids) {
    const url = proxyLov(`${LOV_URL}/${id}`)
    const response = await Rest.delete({ url, identityToken: true })
    success = success && response !== null
  }

  return success
}
